/* eslint-disable default-param-last */
/* eslint-disable camelcase */

import { indexBy, values } from 'ramda';
import { useTranslation } from 'react-i18next';

import { getPreciseFeeCredits } from 'components/trade_fee_credits/trade_fee_info';
import {
  IS_INDIAN_EXCHANGE,
  MARGIN_TYPES,
  OFFER_TYPES_MAPPING,
} from 'constants/constants';
import { ORDER_TYPE, PLACE_ORDER_TYPE } from 'constants/enums';
import { convertExponentialToDecimal } from 'helpers/assetUtils';
import { round_by_tick_size } from 'helpers/formulas';
import {
  getZeroFeeOfferEstimatedFee,
  isEligibleForZeroFeeOptionsOffer,
} from 'helpers/offers';
import {
  cropAfterDecimals,
  discountCalculator,
  getDurationFromNow,
  isFutures,
  isKycRefreshExpired,
  isKycRequiredDeadlineCrossed,
  isOptions,
} from 'helpers/utils';
import { ContractType } from 'types/IProducts';
import Box from 'UIKit/Box';

export const disablePlaceOrderComponents = (isBuyActionState, contractType) => {
  if (isBuyActionState && isOptions(contractType)) {
    return true;
  }
  return false;
};

export const showCalculator = contractType => {
  switch (contractType) {
    case 'call_options':
    case 'put_options':
    case 'move_options':
    case 'options_combos':
    case 'turbo_put_options':
    case 'turbo_call_options':
    case 'interest_rate_swaps':
    case 'spreads':
    case 'spot':
    case 'tracker':
      return false;
    default:
      return true;
  }
};

export const showLeverageSlider = (isBuyActionState, product) => {
  const { product_specs, contract_type } = product;
  switch (contract_type) {
    case 'futures':
    case 'perpetual_futures':
      return true;
    case 'call_options':
    case 'put_options':
    case 'move_options':
    case 'options_combos':
      return !isBuyActionState;
    case 'turbo_put_options':
    case 'turbo_call_options':
      return false;
    case 'interest_rate_swaps':
      if (isBuyActionState) {
        return !!Number(product_specs.floating_rate_min < 0);
      }
      return true;

    case 'spot':
    case 'tracker':
      return false;
    default:
      return true;
  }
};

export const sellOrdersNotAllowed = (contractType, isBuyActionState) => {
  switch (contractType) {
    case 'turbo_put_options':
    case 'turbo_call_options':
      return !isBuyActionState;
    default:
      return false;
  }
};

export const showBracketOrderSection = (
  contractType,
  isBuyActionState,
  userData = {},
  availableBalance,
  pfThreshold
) => {
  const isKycExpired = isKycRefreshExpired(userData);
  const isKycRequired = isKycRequiredDeadlineCrossed(userData);

  // we are enabling reduce only mode if kyc_refresh is expired or kyc_required deadline is crossed
  // hence, removing bracketorder section to show a message
  if (
    isKycExpired ||
    isKycRequired ||
    (userData.margin_mode === MARGIN_TYPES.PORTFOLIO && availableBalance < pfThreshold)
  ) {
    return false;
  }

  switch (contractType) {
    case 'turbo_put_options':
    case 'turbo_call_options':
      return !!isBuyActionState;
    case 'spreads':
    case 'spot':
    case 'interest_rate_swaps':
      return false;
    default:
      return true;
  }
};

const getOrderMetricsLabel = ({ contractType = '' }) => {
  const isProductAnFuture = isFutures(contractType);
  const isProductAnOption = isOptions(contractType);
  if (isProductAnFuture) return 'notional';
  if (isProductAnOption) return 'orderSize';
  return 'orderValue';
};

const getFormattedLiquidationPrice = ({
  isProductAnOption,
  isBuyActionState,
  liquidationPrice,
  productTickSize,
  priceSymbol,
  customFormatting = null,
  marginType,
}) => {
  // when product is an option
  //  portfolio mode (long & short) and cross mode (long & short and isolated mode long,
  //  we are showing NA
  if (
    (isProductAnOption &&
      [MARGIN_TYPES.CROSS, MARGIN_TYPES.PORTFOLIO].includes(marginType)) ||
    (isProductAnOption && isBuyActionState && marginType === MARGIN_TYPES.ISOLATED)
  ) {
    return 'NA';
  }

  if (customFormatting) {
    return (
      <>
        {!liquidationPrice
          ? '-'
          : customFormatting(
              Number(round_by_tick_size(liquidationPrice, productTickSize))
            )}{' '}
        <Box as="span" textColor="secondary">
          {priceSymbol}
        </Box>
      </>
    );
  }

  return (
    <>
      {!liquidationPrice ? '-' : round_by_tick_size(liquidationPrice, productTickSize)}{' '}
      <Box as="span" textColor="secondary">
        {priceSymbol}
      </Box>
    </>
  );
};

export const showMakerTakerPopup = contractType => {
  if (contractType === ContractType.Tracker) return false;
  return true;
};

const LocalizedValue = ({ localeKey, options }) => {
  const { t } = useTranslation(['trading']);
  return t(`trading:${localeKey}`, options);
};

const applyGST = value => {
  if (IS_INDIAN_EXCHANGE) {
    const gstFactor = 0.18; // 18% GST for Indian exchange
    return value * (1 + gstFactor); // Apply 18% GST if the exchange is Indian
  }
  return value; // No GST if the exchange is not Indian
};

const calculateEstimatedTradingFee = ({
  price,
  contract_size,
  size,
  settling_asset,
  spotPrice,
  maker_commission,
  taker_commission,
  postOnly = false,
  contractType,
  estimatedExecPrice,
  orderType,
  notional_type,
}) => {
  const isProductAFuture = isFutures(contractType);
  let priceToUseForNotional = null;
  let effectiveCommission = null;

  // Decide the price to use for notional
  if (isProductAFuture) {
    if (orderType === PLACE_ORDER_TYPE.MARKET || orderType === ORDER_TYPE.MARKET) {
      priceToUseForNotional = estimatedExecPrice;
    } else {
      priceToUseForNotional = price;
    }
  } else {
    priceToUseForNotional = spotPrice; // Use spot price for options
  }

  // Decide the commission to use
  if (postOnly || orderType === PLACE_ORDER_TYPE.MAKER_ONLY) {
    effectiveCommission = maker_commission; // Maker commission if Maker Only order and postOnly,now renamed to maker, is checked.
  } else {
    effectiveCommission = taker_commission; // Taker commission otherwise
  }

  const feeByPremium = Number(contract_size) * Math.abs(size) * Number(price);

  const contractPrice =
    notional_type === 'inverse' ? 1 / priceToUseForNotional : priceToUseForNotional;

  const feeByNotional =
    contract_size *
    Math.abs(size) *
    Number(effectiveCommission) *
    convertExponentialToDecimal(contractPrice);

  const tradingFee = isProductAFuture
    ? feeByNotional
    : Math.min(feeByNotional, 0.1 * feeByPremium);

  let estTradingFee = applyGST(tradingFee);
  const unformattedEstTradingFees = estTradingFee;
  const precision = estTradingFee <= 0.01 ? 4 : settling_asset.minimum_precision;

  estTradingFee = cropAfterDecimals(
    convertExponentialToDecimal(estTradingFee),
    precision
  );

  return {
    label: (
      <LocalizedValue
        localeKey={IS_INDIAN_EXCHANGE ? 'estTradingFeeWithGST' : 'estTradingFee'}
      />
    ),
    value: (
      <>
        {estTradingFee}{' '}
        <Box as="span" textColor="secondary">
          {settling_asset.symbol}
        </Box>
      </>
    ),
    id: 'estTradingFee',
    className: 'estimated-trading-fees',
    unformattedEstTradingFees,
  };
};

const calculateEstimatedTradingFeeAfterVIPProgramBenefits = (
  tradingFee,
  vip_discount_factor,
  feeByPremium
) => {
  let estimatedTradingFee;
  if (tradingFee !== 0.1 * Number(feeByPremium)) {
    estimatedTradingFee = discountCalculator(Number(vip_discount_factor), tradingFee);
  } else {
    estimatedTradingFee = tradingFee;
  }
  return estimatedTradingFee;
};

const calculateEstimatedTradingFeeAfterDeltaCashCredits = (tradingFee, balances) => {
  let estimatedTradingFee;
  let bottomText;
  const balancesBySymbol = indexBy(balance => balance.asset.symbol, values(balances));
  const [, , feeCreditsUSDT] = getPreciseFeeCredits(balancesBySymbol);

  if (Number(feeCreditsUSDT) > 0) {
    const applicableCoveragePercentage = IS_INDIAN_EXCHANGE ? 0.15 : 0.25; // global it is 25% and India 15%
    const offset = Math.min(
      Number(applicableCoveragePercentage * tradingFee),
      feeCreditsUSDT
    );

    estimatedTradingFee = Number(tradingFee) - offset;

    bottomText = (
      <LocalizedValue
        localeKey="partialTradingFeeDeduction"
        options={{ offsetValue: cropAfterDecimals(offset, 2) }}
      />
    );
  } else {
    bottomText = null;
  }
  return { estimatedTradingFee, bottomText };
};

const calculateEstimatedTradingFeeAfterApplicableSettlementPeriod = (
  estTradingFee,
  settlement_date,
  activeOffers,
  appliedOffers,
  side,
  settling_asset
) => {
  const duration = getDurationFromNow(settlement_date);
  const isLessThan12Hours = duration.days() === 0 && duration.hours() < 12;

  // No fee will be charged if buy order is placed within 12 hours of the settlement date.
  // Will remove this when offer gets over.
  if (
    isLessThan12Hours &&
    side === 'buy' &&
    isEligibleForZeroFeeOptionsOffer(activeOffers, appliedOffers)
  ) {
    const zeroFeeOffer = activeOffers[OFFER_TYPES_MAPPING.ZERO_FEE_OPTIONS_BUYING];
    const claimedZeroFeeOffer =
      appliedOffers[OFFER_TYPES_MAPPING.ZERO_FEE_OPTIONS_BUYING];
    const feeToBeCharged = getZeroFeeOfferEstimatedFee({
      offer: zeroFeeOffer,
      claimedOffer: claimedZeroFeeOffer,
      estimatedFee: estTradingFee,
    });
    const formattedFeeToBeCharged = cropAfterDecimals(
      feeToBeCharged,
      settling_asset && settling_asset.minimum_precision
        ? settling_asset.minimum_precision
        : 2
    );

    return {
      label: <LocalizedValue localeKey="estTradingFee" />,
      strikedValue:
        Number(estTradingFee) !== 0
          ? `${cropAfterDecimals(
              estTradingFee,
              settling_asset && settling_asset.minimum_precision
                ? settling_asset.minimum_precision
                : 2
            )} ${settling_asset.symbol}`
          : null,
      value: (
        <>
          {formattedFeeToBeCharged}{' '}
          <Box as="span" textColor="secondary">
            {settling_asset.symbol}
          </Box>
        </>
      ),
      className: 'trading-fee-estimate',
      bottomText: (
        <LocalizedValue
          localeKey={feeToBeCharged > 0 ? 'estTradingFee' : 'feeNotApplicable'}
        />
      ),
    };
  }

  return undefined;
};

export {
  getOrderMetricsLabel,
  getFormattedLiquidationPrice,
  applyGST,
  calculateEstimatedTradingFee,
  calculateEstimatedTradingFeeAfterVIPProgramBenefits,
  calculateEstimatedTradingFeeAfterDeltaCashCredits,
  calculateEstimatedTradingFeeAfterApplicableSettlementPeriod,
};
